import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWarehouse,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

import stockpileService from '../../services/stockpileService';
import StockpileDetailModal from './StockpileDetailModal';
import StockpileSparkline from './StockpileSparkline';
import './StockpilePanel.css';

const RECENT_LIMIT = 3;
const SPARKLINE_DAYS = 30;

const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num
        .toLocaleString('en-US', { maximumFractionDigits: 2 })
        .replace(/,/g, ' ');
};

const formatRelativeTime = (iso) => {
    if (!iso) return null;
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    if (Number.isNaN(diffMs)) return null;
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString();
};

const formatShortDate = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const TYPE_LABELS = {
    opening_balance: 'Opening',
    inbound: 'Inbound',
    outbound: 'Outbound',
    adjustment: 'Adjustment',
    void: 'Void',
};

const SOURCE_LABELS = {
    manual: 'Manual',
    opening: 'Initial',
    delivery: 'Delivery',
    invoice: 'Invoice',
};

const movementLabel = (movement) => {
    const type = TYPE_LABELS[movement.movement_type] || movement.movement_type;
    const source = SOURCE_LABELS[movement.source] || movement.source;
    return `${type} · ${source}`;
};

const unitLabel = (movement, fallback = '') => {
    return movement?.unit_name || movement?.unit_code || fallback;
};

const deltaClass = (movement) => {
    const value = Number(movement.delta);
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
};

const formatDelta = (movement, fallbackUnit = '') => {
    const value = Number(movement.delta);
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    const magnitude = formatNumber(Math.abs(value));
    return `${sign}${magnitude} ${unitLabel(movement, fallbackUnit)}`.trim();
};

/**
 * Compact stockpile preview that lives on the variant card. The whole panel
 * is clickable and opens the big StockpileDetailModal. All actions
 * (inbound / outbound / void / full history) live in the modal.
 */
const StockpilePanel = ({ variant }) => {
    const variantId = variant?.id;
    const variantUnitDisplay = variant?.unit_name || variant?.unit_code || '';
    const variantHasUnit = Boolean(variant?.unit);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [balance, setBalance] = useState(null);
    const [movements, setMovements] = useState([]);
    const [series, setSeries] = useState([]);
    const [detailOpen, setDetailOpen] = useState(false);

    const refresh = useCallback(async () => {
        if (!variantId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await stockpileService.getStockpile(variantId, {
                limit: RECENT_LIMIT,
                includeSeries: true,
                days: SPARKLINE_DAYS,
            });
            setBalance(data.balance);
            setMovements(data.movements || []);
            setSeries(data.series || []);
        } catch (err) {
            console.error('Stockpile load failed', err);
            setError('Could not load stockpile.');
        } finally {
            setLoading(false);
        }
    }, [variantId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onHand = balance ? Number(balance.on_hand) : null;
    const isNegative = onHand !== null && onHand < 0;
    const lastWhen = balance?.last_movement_at ? formatRelativeTime(balance.last_movement_at) : null;
    const balanceUnit = balance?.unit_name || balance?.unit_code || variantUnitDisplay;

    const handleOpen = () => setDetailOpen(true);
    const handleKeyOpen = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
        }
    };

    return (
        <div className="stockpile-section">
            <div
                className="stockpile-panel clickable"
                role="button"
                tabIndex={0}
                onClick={handleOpen}
                onKeyDown={handleKeyOpen}
                title="Open stockpile detail"
            >
                <div className="stockpile-header">
                    <div className="stockpile-header-top">
                        <div className="stockpile-header-left">
                            <FontAwesomeIcon icon={faWarehouse} className="stockpile-icon" />
                            <span className="stockpile-title">Stockpile</span>
                        </div>
                        <div className="stockpile-header-right">
                            {lastWhen && (
                                <span className="stockpile-balance-meta">Updated {lastWhen}</span>
                            )}
                            <FontAwesomeIcon icon={faChevronRight} className="stockpile-chevron" />
                        </div>
                    </div>
                    <div className={`stockpile-balance-value${isNegative ? ' negative' : ''}`}>
                        {balance ? formatNumber(onHand) : '—'}
                        <span className="stockpile-balance-unit">{balanceUnit}</span>
                    </div>
                </div>

                {!variantHasUnit && (
                    <div className="stockpile-warning">
                        Set a unit on the variant before tracking stockpile movements.
                    </div>
                )}

                {variantHasUnit && isNegative && (
                    <div className="stockpile-warning">
                        On-hand balance is negative — pending paperwork?
                    </div>
                )}

                {error && <div className="stockpile-error">{error}</div>}

                {variantHasUnit && series.length > 0 && (
                    <StockpileSparkline series={series} unit={balanceUnit} />
                )}

                {loading && !balance ? (
                    <div className="stockpile-loading">Loading stockpile…</div>
                ) : movements.length > 0 ? (
                    <div className="stockpile-recent">
                        {movements.map((m) => (
                            <div
                                key={m.id}
                                className={`stockpile-row${m.is_voided ? ' voided' : ''}`}
                                title={m.notes || ''}
                            >
                                <span className={`stockpile-row-delta ${deltaClass(m)}`}>
                                    {formatDelta(m, balanceUnit)}
                                </span>
                                <span className="stockpile-row-meta">{movementLabel(m)}</span>
                                <span className="stockpile-row-date">{formatShortDate(m.occurred_at)}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="stockpile-empty">No stockpile activity yet.</div>
                )}

                <div className="stockpile-open-hint">
                    Click for full chart, history, and to record movements
                </div>
            </div>

            <StockpileDetailModal
                isOpen={detailOpen}
                variant={variant}
                onClose={() => setDetailOpen(false)}
                onChanged={refresh}
            />
        </div>
    );
};

export default StockpilePanel;
